import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { fetchSavedColumnWidths, saveColumnWidths, type ColumnWidthsTable } from '../lib/userPreferencesApi';

export interface ColumnWidthsState {
  /** This person's saved widths for the table, by column key. */
  widths: Record<string, number>;
  /** A drag in progress: on screen only. */
  resize: (key: string, px: number) => void;
  /** The drag ended: one PUT, or none if nothing changed since the last save. */
  commit: () => void;
  /** Back to this column's default, saved. */
  reset: (key: string) => void;
}

const sameWidths = (a: Record<string, number>, b: Record<string, number>) => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
};

/**
 * D178: a table's column widths, saved per signed-in user on mouse release and applied to every
 * document that person opens. A save replaces the whole map, so it never runs until the load has
 * answered: after a failed load nothing is saved. A failed load or save says so in a toast.
 */
export function useColumnWidths(table: ColumnWidthsTable): ColumnWidthsState {
  const [widths, setWidths] = useState<Record<string, number>>({});
  // The latest widths and what the server holds, read through refs so a stale closure never saves old widths.
  const latest = useRef(widths);
  const saved = useRef(widths);
  // The load's outcome: true = loaded, false = failed or cancelled.
  const loaded = useRef<Promise<boolean>>(Promise.resolve(false));
  // The toast context is a new object whenever a toast shows; it must not re-run the load.
  const currentToast = useToast();
  const toast = useRef(currentToast);
  toast.current = currentToast;

  const show = useCallback((next: Record<string, number>) => {
    latest.current = next;
    setWidths(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loaded.current = (async () => {
      try {
        const stored = (await fetchSavedColumnWidths(table)) ?? {};
        if (cancelled) return false;
        saved.current = stored;
        // Under what is on screen: an answer arriving after a drag began never overwrites it, and
        // the other columns' saved widths survive that drag's save.
        show({ ...stored, ...latest.current });
        return true;
      } catch (err) {
        if (!cancelled) toast.current.error('Your saved column widths could not be loaded, so this table is at its default widths and changes will not be saved. Reload to try again.');
        return false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [table, show]);

  // ponytail: every mouse move re-renders the page: 150-190ms per move on a 200-line invoice in a dev build. Apply the drag to the <col> directly if that drags.
  const resize = useCallback((key: string, px: number) => {
    if (latest.current[key] !== px) show({ ...latest.current, [key]: px });
  }, [show]);

  const commit = useCallback(async () => {
    if (!(await loaded.current)) return;
    const sending = latest.current;
    if (sameWidths(sending, saved.current)) return;
    // Counted as saved from the moment it leaves, so a reset during this save still sends its own map.
    const previous = saved.current;
    saved.current = sending;
    try {
      await saveColumnWidths(table, sending);
    } catch (err) {
      if (saved.current === sending) saved.current = previous;
      toast.current.error('Your column widths were not saved. Try again.');
    }
  }, [table]);

  const reset = useCallback((key: string) => {
    const { [key]: _gone, ...rest } = latest.current;
    show(rest);
    commit();
  }, [show, commit]);

  return { widths, resize, commit, reset };
}
